import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Avatar,
    Divider,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PositionModalData, AwardObject, UpgradeAward, CloneReward, SpecialAward } from './types';

interface Props {
    open: boolean;
    onClose: () => void;
    data: PositionModalData | null;
    currency: string;
}

const currencySymbol = (currency: string) =>
    currency?.toLowerCase() === 'btc' ? 'BTC' : 'USDT';

const AwardRow = ({
    label,
    color,
    award,
    currency,
}: {
    label: string;
    color: string;
    award: AwardObject;
    currency: string;
}) => (
    <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        py={0.75}
        px={1}
        mb={0.5}
        sx={{ bgcolor: `${color}18`, borderRadius: 1, border: `1px solid ${color}40` }}
    >
        <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Box>
        <Box textAlign="right">
            <Typography variant="body2" fontWeight={600} color={color}>
                +{award.amount} {currencySymbol(currency)}
            </Typography>
            <Typography variant="caption" color="text.disabled">
                → {award.login}
            </Typography>
        </Box>
    </Box>
);

const UpgradeRow = ({ upgrade, currency }: { upgrade: UpgradeAward; currency: string }) => (
    <Box
        py={0.75}
        px={1}
        mb={0.5}
        sx={{ bgcolor: '#44A43718', borderRadius: 1, border: '1px solid #44A43740' }}
    >
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#44A437', flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                    Апгрейд → {upgrade.next_program} {upgrade.next_level}
                </Typography>
            </Box>
            <Typography variant="body2" fontWeight={600} color="#44A437">
                +{upgrade.amount} {currencySymbol(currency)}
            </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ pl: 2.5 }}>
            → {upgrade.login}
        </Typography>
    </Box>
);

const CloneRow = ({ cloneReward }: { cloneReward: CloneReward }) => (
    <Box
        py={0.75}
        px={1}
        mb={0.5}
        sx={{ bgcolor: '#FF6B2B18', borderRadius: 1, border: '1px solid #FF6B2B40' }}
    >
        <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FF6B2B', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
                {cloneReward.description}
            </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ pl: 2.5 }}>
            → {cloneReward.login}
        </Typography>
    </Box>
);

const SpecialRow = ({ special }: { special: SpecialAward[] }) => (
    <>
        {special.map((s, i) => (
            <Box
                key={i}
                py={0.75}
                px={1}
                mb={0.5}
                sx={{ bgcolor: '#FFD70018', borderRadius: 1, border: '1px solid #FFD70040' }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#FFD700', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary">
                            {s.description}
                        </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} color="#FFD700">
                        ×{s.amount}
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.disabled" sx={{ pl: 2.5 }}>
                    → {s.login}
                </Typography>
            </Box>
        ))}
    </>
);

export const RewardsModal = ({ open, onClose, data, currency }: Props) => {
    if (!data) return null;

    // ── Mentor modal ──────────────────────────────────────────────────────
    if (data.kind === 'mentor') {
        const pos = data.position;
        const avatarSrc = pos.avatar ? pos.avatar : undefined;

        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography fontWeight={600}>Ментор</Typography>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                        <Avatar src={avatarSrc} sx={{ width: 72, height: 72, mb: 1 }}>
                            {!avatarSrc && (pos.login?.[0]?.toUpperCase() || '?')}
                        </Avatar>
                        <Typography fontWeight={600} variant="h6">
                            {pos.login || '—'}
                        </Typography>
                        {pos.kurator && (
                            <Typography variant="caption" color="text.secondary">
                                Куратор: {pos.kurator}
                            </Typography>
                        )}
                    </Box>

                    {pos.link && (
                        <Button
                            variant="contained"
                            fullWidth
                            href={pos.link}
                            target="_blank"
                            startIcon={<OpenInNewIcon />}
                            sx={{ textTransform: 'none' }}
                        >
                            Перейти к столу
                        </Button>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    // ── Slot modal ────────────────────────────────────────────────────────
    const { reward, position, slotIndex } = data;
    const pos = position && typeof position !== 'boolean' ? position : null;
    const avatarSrc = pos?.avatar ? (pos.avatar as string) : undefined;

    const hasRewards =
        reward.main_award ||
        reward.referal_1 ||
        reward.referal_2 ||
        reward.referal_3 ||
        reward.clone_balance ||
        reward.upgrade ||
        reward.clone_reward ||
        (reward.special && reward.special.length > 0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                    <Typography fontWeight={600}>Слот {slotIndex}</Typography>
                    <Chip
                        label={reward.filled ? 'Занят' : 'Пустой'}
                        size="small"
                        color={reward.filled ? 'success' : 'default'}
                        sx={{ mt: 0.25 }}
                    />
                </Box>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {/* User info if slot filled */}
                {pos && (
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                        <Avatar src={avatarSrc} sx={{ width: 48, height: 48 }}>
                            {!avatarSrc && (pos.login?.[0]?.toUpperCase() || '?')}
                        </Avatar>
                        <Typography fontWeight={600}>{pos.login || '—'}</Typography>
                    </Box>
                )}

                {/* Rewards */}
                {hasRewards ? (
                    <>
                        <Divider sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                Вознаграждения
                            </Typography>
                        </Divider>

                        {reward.main_award && (
                            <AwardRow
                                label="Основное"
                                color="#F59E0B"
                                award={reward.main_award}
                                currency={currency}
                            />
                        )}
                        {reward.referal_1 && (
                            <AwardRow
                                label="Реферальное 1 ур."
                                color="#3B82F6"
                                award={reward.referal_1}
                                currency={currency}
                            />
                        )}
                        {reward.referal_2 && (
                            <AwardRow
                                label="Реферальное 2 ур."
                                color="#60A5FA"
                                award={reward.referal_2}
                                currency={currency}
                            />
                        )}
                        {reward.referal_3 && (
                            <AwardRow
                                label="Реферальное 3 ур."
                                color="#93C5FD"
                                award={reward.referal_3}
                                currency={currency}
                            />
                        )}
                        {reward.clone_balance && (
                            <AwardRow
                                label="Баланс клонов"
                                color="#A855F7"
                                award={reward.clone_balance}
                                currency={currency}
                            />
                        )}
                        {reward.upgrade && (
                            <UpgradeRow upgrade={reward.upgrade} currency={currency} />
                        )}
                        {reward.clone_reward && (
                            <CloneRow cloneReward={reward.clone_reward} />
                        )}
                        {reward.special && reward.special.length > 0 && (
                            <SpecialRow special={reward.special} />
                        )}
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        Нет данных о вознаграждениях
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} size="small">
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};